import React, { useEffect, useState } from "react";
import { Grid, TextField, Autocomplete, Chip } from "@mui/material";
import { Button } from "antd";
import AdvancedTable from "../../sharedComponents/AdvancedTable/AdvancedTable";
import { getClientTaskReport, downloadClientTaskReport } from "../../services/reports.service";
import { getUser } from "../../services/user.service";
import { getClientProjectReports } from "../../services/client.service";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import { ISSUETYPE } from "../../common/constant/constant";
import ClientTaskDetailsDialog from "./Components/ClientTaskDetailsDialog";

const { RangePicker } = DatePicker;

const ClientTaskReport = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [projects, setProjects] = useState([]);
    const [selectedProjects, setSelectedProjects] = useState([]);
    const [issueType, setIssueType] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const initialMonth = dayjs();
    const [dateRange, setDateRange] = useState({
        startDate: initialMonth.startOf("month"),
        endDate: initialMonth,
    });
    const [data, setData] = useState([]);
    const [dateColumns, setDateColumns] = useState([]);
    // State for Drill Down Dialog
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogData, setDialogData] = useState(null);

    const issueTypeOptions = [
        { label: "All", value: null },
        { label: "Bug", value: ISSUETYPE.BUG },
        { label: "Task", value: ISSUETYPE.TASK },
        { label: "Meeting", value: ISSUETYPE.MEETING },
        { label: "Testing", value: ISSUETYPE.TESTING },
        { label: "Designing", value: ISSUETYPE.DESIGNING },
        { label: "Others", value: ISSUETYPE.OTHERS },
        { label: "Deployment", value: ISSUETYPE.DEPLOYMENT },
    ];

    // Fetch all users
    // useEffect(() => {
    //     const fetchUsers = async () => {
    //         try {
    //             const response = await getUser();
    //             if (response?.data?.data) {
    //                 setUsers(response.data.data.map((u) => ({
    //                     ...u,
    //                     name: `${u.firstName} ${u.lastName}`,
    //                 })));
    //             }
    //         } catch (error) {
    //             console.error("Error fetching users:", error);
    //         }
    //     };
    //     fetchUsers();
    // }, []);

    // Fetch projects
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                // Use getClientProjectReports instead of getAllProject to filter client projects correctly
                const response = await getClientProjectReports();
                if (response?.data?.data) {
                    setProjects(response.data.data);
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
            }
        };
        fetchProjects();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const projectIds = selectedProjects.length > 0
                ? selectedProjects.map(p => p._id).join(',')
                : null;

            const response = await getClientTaskReport({
                startDate: dateRange.startDate.startOf("day").toISOString(),
                endDate: dateRange.endDate.endOf("day").toISOString(),
                userId: selectedUser?._id,
                issueType: issueType,
                projectIds: projectIds,
            });

            if (response?.data) {
                setData(response.data);
            }
        } catch (error) {
            console.error("Error fetching report:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            fetchData();
        }
    }, [dateRange, selectedUser, issueType, selectedProjects]); // Added dependencies to auto-refresh

    // Collect unique dates from data
    useEffect(() => {
        const days = new Set();
        data.forEach((project) => {
            project.dates.forEach((d) => days.add(d.day));
        });
        setDateColumns([...days].sort());
    }, [data]);

    // Prepare table columns
    const columns = [
        { id: "projectName", label: "Project Name", isTotal: false },
        ...dateColumns.map((day) => {
            const dayLabel = dayjs(day).format("MMM-DD");
            // Mark date columns as clickable
            return { id: day, label: dayLabel, isTotal: true, totalType: "time", isClickable: true };
        }),
        { id: "Total", label: "Total Hours", isTotal: true, isTotalColumn: true, totalType: "time" },
    ];
    const getAllDatesInRange = (start, end) => {
        const dates = [];
        let current = start.startOf("day");
        const last = end.startOf("day");
        while (current.isBefore(last) || current.isSame(last)) {
            dates.push(current.format("YYYY-MM-DD"));
            current = current.add(1, "day");
        }
        return dates;
    };

    // In your useEffect for columns
    useEffect(() => {
        if (dateRange.startDate && dateRange.endDate) {
            setDateColumns(getAllDatesInRange(dateRange.startDate, dateRange.endDate));
        } else {
            setDateColumns([]);
        }
    }, [dateRange]);

    const transformedData = data.map((project) => {
        // Store projectId in the row data for drill-down usage
        const row = {
            projectName: project.projectName,
            projectId: project.projectId // Ensure backend returns this
        };
        let totalMinutes = 0;

        dateColumns.forEach((day) => {
            const entry = project.dates.find((d) => d.day === day);
            if (entry) {
                const hours = Math.floor(entry.totalHours);
                const mins = Math.round((entry.totalHours % 1) * 100);
                totalMinutes += hours * 60 + mins;

                row[day] = `${hours}.${mins.toString().padStart(2, "0")}`;
            } else {
                row[day] = "0.00";
            }
        });

        const totalHours = Math.floor(totalMinutes / 60);
        const totalMins = totalMinutes % 60;
        row.Total = `${totalHours}.${totalMins.toString().padStart(2, "0")}`;

        return row;
    });
    transformedData.sort((a, b) => a.projectName.localeCompare(b.projectName));

    const handleDownload = async () => {
        setDownloadLoading(true);
        try {
            const projectIds = selectedProjects.length > 0
                ? selectedProjects.map(p => p._id).join(',')
                : null;

            const response = await downloadClientTaskReport({
                startDate: dateRange.startDate
                    ? dateRange.startDate.startOf("day").toISOString()
                    : null,
                endDate: dateRange.endDate
                    ? dateRange.endDate.endOf("day").toISOString()
                    : null,
                userId: selectedUser?._id || "All",
                issueType: issueType !== undefined && issueType !== null ? issueType : "All",
                projectIds: projectIds,
            });

            // Handle blob response
            const blob = new Blob([response], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;

            const startStr = dateRange.startDate.format("YYYY-MM-DD");
            const endStr = dateRange.endDate.format("YYYY-MM-DD");
            link.setAttribute("download", `Client_Task_Report_${startStr}_to_${endStr}.xlsx`);

            document.body.appendChild(link);
            link.click();

            link.parentNode.removeChild(link);
        } catch (error) {
            console.error("Error downloading report:", error);
            alert("Failed to download report");
        } finally {
            setDownloadLoading(false);
        }
    };

    // Handle cell click for drill down
    const handleCellClick = (row, column) => {
        // Only open if the column is a date column (isClickable is true)
        // and if the cell value is not "0.00" (has hours)
        if (column.isClickable && row[column.id] !== "0.00") {
            setDialogData({
                projectId: row.projectId,
                projectName: row.projectName,
                date: column.id, // Column ID is the date string "YYYY-MM-DD"
            });
            setDialogOpen(true);
        }
    };

    const tableConfig = {
        isAction: false,
        isEdit: false,
        isMore: false,
        isAdmin: false,
    };
    return (
        <div className="app-container">
            <Grid container spacing={4} mb={2}>
                <Grid item>
                    <RangePicker
                        value={[
                            dateRange.startDate ? dayjs(dateRange.startDate) : null,
                            dateRange.endDate ? dayjs(dateRange.endDate) : null,
                        ]}
                        onChange={(dates) => {
                            const [start, end] = dates || [];
                            if (start && end) {
                                setDateRange({
                                    startDate: dayjs(start).startOf("day"),
                                    endDate: dayjs(end).endOf("day"),
                                });
                            } else {
                                const initialMonth = dayjs();
                                setDateRange({
                                    startDate: initialMonth.startOf("month"),
                                    endDate: initialMonth.endOf("month"),
                                });
                            }
                        }}
                        className="datePicker"
                        format={"DD-MM-YYYY"}
                        style={{ width: 280 }}
                    />
                </Grid>
                {/* <Grid item>
                    <Autocomplete
                        options={users}
                        getOptionLabel={(u) => u.name || ""}
                        value={selectedUser}
                        onChange={(e, val) => setSelectedUser(val)}
                        renderInput={(params) => (
                            <TextField {...params} label="Select User" />
                        )}
                        sx={{ width: 250 }}
                    />
                </Grid>
                <Grid item>
                    <Autocomplete
                        value={
                            issueTypeOptions.find((opt) => opt.value === issueType) ||
                            issueTypeOptions[0]
                        }
                        options={issueTypeOptions}
                        onChange={(e, v) => setIssueType(v?.value)}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => (
                            <TextField {...params} placeholder="Select Issue Type" />
                        )}
                        sx={{ width: 250 }}
                    />
                </Grid> */}
                <Grid item>
                    <Autocomplete
                        multiple
                        limitTags={1}
                        disableCloseOnSelect
                        options={projects}
                        getOptionLabel={(p) => p.name || ""}
                        value={selectedProjects}
                        onChange={(e, val) => setSelectedProjects(val)}
                        renderTags={(value) => (
                            <div style={{
                                fontSize: '14px',
                                color: '#666',
                                display: 'flex',
                                alignItems: 'center',
                                minHeight: '24px'
                            }}>
                                {value.length > 0 ? `${value.length} project${value.length > 1 ? 's' : ''} selected` : ''}
                            </div>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Projects"
                                placeholder={selectedProjects.length === 0 ? "Select Projects" : ""}
                            />
                        )}
                        sx={{
                            width: 300,
                        }}
                    />
                </Grid>
                <Grid item>
                    <Button
                        disabled={loading}
                        type="primary"
                        onClick={handleDownload}
                        loading={downloadLoading}
                        style={{
                            backgroundColor: "#008080",
                            borderColor: "#008080",
                            height: "40px",
                        }}
                    >
                        Download Excel
                    </Button>
                </Grid>
            </Grid>

            <AdvancedTable
                columns={columns}
                data={transformedData}
                openLoader={loading}
                isPagination={false}
                tableConfig={{
                    ...tableConfig, // Keep existing tableConfig properties
                    isCellClickable: true // Optional config if AdvancedTable uses it globally
                }}
                onCellClick={handleCellClick}
            />

            {/* Drill Down Dialog */}
            <ClientTaskDetailsDialog
                open={dialogOpen}
                handleClose={() => setDialogOpen(false)}
                data={dialogData}
            />
        </div>
    );
};

export default ClientTaskReport;
